import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { apiService } from '../../services/api';

const blinkAnimation = {
  animation: 'pageFlash 450ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

interface DataPreviewPanelProps {
  batchId: string;
  fileName: string;
  /** Original source identifier (file name, sheet name, DB table) used for the preview API request.
   *  Falls back to fileName if not provided. */
  fileOrigin?: string;
  expanded: boolean;
}

const DataPreviewPanel: React.FC<DataPreviewPanelProps> = ({
  batchId,
  fileName,
  fileOrigin,
  expanded,
}) => {
  const [limit, setLimit] = useState(25);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);

  // Use the original source identifier for the API request (the backend needs the
  // actual file name / table name to locate the data in storage)
  const previewFileName = fileOrigin || fileName;

  const { data, isLoading, error, refetch } = useQuery(
    ['source-preview', batchId, previewFileName, limit],
    () => apiService.getSourcePreview(batchId, previewFileName, limit),
    {
      enabled: expanded && !!batchId && !!previewFileName,
      refetchOnWindowFocus: false,
      staleTime: 60000,
    }
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await refetch();
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 350);
    }
  };

  if (!expanded) return null;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load data preview.
      </Alert>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <Alert severity="info">No data rows available for preview.</Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {data.row_count} of {data.total_rows.toLocaleString()} rows
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Rows:
          </Typography>
          <Select
            size="small"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            sx={{ minWidth: 70 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
          {/* <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button> */}
          <Button
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
            startIcon={
              isRefreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          maxHeight: 400,
          overflow: 'auto',
          ...(isRefreshing ? blinkAnimation : {}),
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100', minWidth: 40 }}>
                #
              </TableCell>
              {data.columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{ fontWeight: 'bold', bgcolor: 'grey.100', whiteSpace: 'nowrap' }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{rowIdx + 1}</TableCell>
                {row.map((cell, cellIdx) => (
                  <TableCell
                    key={cellIdx}
                    sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...(cell === null || cell === undefined
                        ? { bgcolor: 'action.hover', fontStyle: 'italic', color: 'text.disabled' }
                        : {}),
                    }}
                  >
                    {cell === null || cell === undefined ? (
                      <Typography variant="caption" color="text.disabled">
                        null
                      </Typography>
                    ) : (
                      String(cell)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataPreviewPanel;
